import { useEffect, useId, useRef, useState } from 'react';
import type {
  LexiconReportContext,
  LexiconReportInput,
  OwnLexiconReport,
} from '@orborix/shared-types';
import {
  LexiconReportApiError,
  reportContext,
  restoreMeaning,
  sendLexiconReport,
} from '../../lib/lexiconReportsApi.js';
import { reportRequestId } from '../../lib/reportRequestId.js';
import { LibIcArrowR, LibIcCheckC, LibIcChevD, LibIcFlag, LibIcRetry } from './libIcons.js';
import './meaningReport.css';

const statusText: Record<OwnLexiconReport['status'], string> = {
  received: 'Aviso recibido · Espera revisión',
  reviewing: 'Aviso recibido · En revisión',
  paused: 'Aviso recibido · Revisión en pausa',
  needs_review: 'Necesita otra revisión',
  unchanged: 'Aviso revisado · Sin cambios',
  approved_pending: 'Cambio aceptado · Espera la pasada',
  published: 'Cambio publicado',
  rolled_back: 'Publicación revertida · Necesita revisión',
};
interface ReportDraft {
  reason: LexiconReportInput['reason'];
  field?: LexiconReportInput['field'];
  comment: string;
  request: LexiconReportInput | null;
}
function readDraft(key: string): ReportDraft | null {
  try {
    const d = JSON.parse(sessionStorage.getItem(key) ?? 'null') as ReportDraft | null;
    return d &&
      ['wrong', 'inappropriate', 'missing'].includes(d.reason) &&
      typeof d.comment === 'string' &&
      d.comment.length <= 500
      ? d
      : null;
  } catch {
    return null;
  }
}
export function MeaningReport({
  word,
  meaningId,
  context,
  onContext,
  onRestore,
}: {
  word: string;
  meaningId: string;
  context: LexiconReportContext;
  onContext: (c: LexiconReportContext) => void;
  onRestore: () => Promise<void>;
}) {
  const controlId = useId();
  const bodyId = `${controlId}-body`;
  const commentId = `${controlId}-comment`;
  const helpId = `${controlId}-help`;
  const counterId = `${controlId}-counter`;
  const draftKey = `lexicon-report:${context.draftScope ?? word}:${meaningId}`;
  const draft = useRef(readDraft(draftKey));
  const thumb = useRef<HTMLButtonElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<LexiconReportInput['reason']>(
    draft.current?.reason ?? 'wrong',
  );
  const [field, setField] = useState<LexiconReportInput['field']>(draft.current?.field);
  const [comment, setComment] = useState(draft.current?.comment ?? '');
  const [request, setRequest] = useState<LexiconReportInput | null>(
    draft.current?.request?.word === word ? draft.current.request : null,
  );
  const maxLength = reason === 'missing' ? context.limits.missingMax : context.limits.comment;
  const belowMinimum = reason === 'missing' && comment.trim().length < context.limits.missingMin;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const own = showNew
    ? undefined
    : (context.reports.find((r) =>
        selectedReportId
          ? r.id === selectedReportId
          : r.meaningId === meaningId || r.reason === 'missing',
      ) ?? (!context.enabled ? context.reports[0] : undefined));
  const ownId = own?.id;
  const hidden = context.hiddenIds.includes(own?.meaningId ?? meaningId);
  useEffect(() => {
    try {
      if (ownId) sessionStorage.removeItem(draftKey);
      else if (comment || request)
        sessionStorage.setItem(draftKey, JSON.stringify({ reason, field, comment, request }));
    } catch {
      /* Memory still retains the draft if storage is disabled. */
    }
  }, [draftKey, reason, field, comment, request, ownId]);
  const close = () => {
    setOpen(false);
    thumb.current?.focus();
  };
  useEffect(() => {
    if (open && !own) textarea.current?.focus();
  }, [open, own]);
  // Sólo consulta; nunca reenvía al refrescar ni al abrir un caso recibido.
  useEffect(() => {
    if (!open || !ownId) return;
    let disposed = false;
    const refresh = () =>
      void reportContext(word)
        .then((c) => {
          if (!disposed) onContext(c);
        })
        .catch(() => {
          if (!disposed) setError('No pudimos actualizar el estado. Intentá de nuevo.');
        });
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => {
      disposed = true;
      clearInterval(timer);
    };
  }, [open, ownId, word, onContext]);
  const edit = () => {
    setRequest(null);
    setError('');
  };
  async function send() {
    if (busy || !context.version) return;
    const input = request ?? {
      requestId: reportRequestId(),
      dictionary: 'v2' as const,
      word,
      version: context.version,
      reason,
      ...(reason === 'missing'
        ? { context: comment }
        : { meaningId, ...(field && reason === 'wrong' ? { field } : {}), comment }),
    };
    setRequest(input);
    setBusy(true);
    setError('');
    try {
      const report = await sendLexiconReport(input);
      // El acuse sólo nace tras respuesta del commit persistido. Consulta posterior puede fallar sin perderlo.
      onContext({
        ...context,
        reports: [report, ...context.reports.filter((r) => r.id !== report.id)],
        hiddenIds:
          input.reason === 'inappropriate'
            ? [...new Set([...context.hiddenIds, meaningId])]
            : context.hiddenIds,
      });
      setShowNew(false);
      setSelectedReportId(report.id);
      setComment('');
      setRequest(null);
    } catch (e) {
      const code = e instanceof LexiconReportApiError ? e.code : '';
      setError(
        code === 'report_rate_limit' || code === 'new_case_limit'
          ? 'Alcanzaste el límite de avisos. Podés consultar los que ya enviaste.'
          : code === 'catalog_changed'
            ? 'El significado cambió. Cerrá la ficha y volvé a abrirla antes de enviar.'
            : 'No pudimos confirmar el envío. Conservamos tu texto; podés reintentar.',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className="meaning-report"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          close();
        }
      }}
    >
      <button
        disabled={!context.enabled && !own && !hidden && !context.reports.length}
        ref={thumb}
        type="button"
        className="meaning-report-thumb"
        aria-label="Avisar un error"
        title="Avisar un error"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <g transform="translate(0 24) scale(1 -1)">
            <path d="M7 11 L 11 3.5 a 2.2 2.2 0 0 1 2 3.5 L 12.2 9 H 18 a 2 2 0 0 1 1.9 2.6 l -2 6.5 A 2 2 0 0 1 16 19.5 H 7" />
            <path d="M7 10.5 H 4 V 20 H 7 Z" />
          </g>
        </svg>
      </button>
      {!own && !open && context.reports.length > 0 && (
        <button
          className="meaning-report-status"
          type="button"
          onClick={() => {
            setSelectedReportId(context.reports[0]!.id);
            setShowNew(false);
            setOpen(true);
          }}
        >
          Ver avisos enviados
        </button>
      )}
      {own && !open && (
        <button className="meaning-report-status" type="button" onClick={() => setOpen(true)}>
          <LibIcCheckC size={16} />
          <span>{statusText[own.status]}</span>
          <LibIcChevD size={14} />
        </button>
      )}
      {open && (
        <div id={bodyId} className="meaning-report-body">
          {own ? (
            <>
              <div className={`meaning-report-result is-${own.status}`}>
                <span className="meaning-report-result-icon">
                  {own.status === 'needs_review' || own.status === 'rolled_back' ? (
                    <LibIcFlag size={20} />
                  ) : (
                    <LibIcCheckC size={20} />
                  )}
                </span>
                <div>
                  <p className="meaning-report-result-title" role="status">
                    {statusText[own.status]}
                  </p>
                  <p className="meaning-report-note">
                    {own.status === 'approved_pending'
                      ? 'El cambio todavía no está publicado.'
                      : own.status === 'unchanged'
                        ? 'El significado se mantiene.'
                        : own.status === 'published'
                          ? 'El cambio ya se puede consultar en la ficha.'
                          : own.status === 'needs_review' || own.status === 'rolled_back'
                            ? 'No hay una corrección publicada para este aviso.'
                            : 'Tu aviso sigue guardado. Podés consultar su estado acá.'}
                  </p>
                </div>
              </div>
              {context.reports.length > 1 && (
                <label className="meaning-report-field">
                  <span>Avisos enviados</span>
                  <span className="meaning-report-select">
                    <select value={own.id} onChange={(e) => setSelectedReportId(e.target.value)}>
                      {context.reports.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.reason === 'missing'
                            ? 'Falta un significado'
                            : r.reason === 'inappropriate'
                              ? 'Es impropio'
                              : 'Está mal'}{' '}
                          · {statusText[r.status]}
                        </option>
                      ))}
                    </select>
                    <LibIcChevD size={15} />
                  </span>
                </label>
              )}
              {hidden && (
                <div className="meaning-report-protection">
                  <p>La protección sigue activa sólo para vos.</p>
                  <button
                    className="meaning-report-secondary"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setBusy(true);
                      void restoreMeaning(word, own?.meaningId ?? meaningId)
                        .then(onRestore)
                        .then(() => reportContext(word))
                        .then(onContext)
                        .catch(() => setError('No pudimos volver a mostrarlo.'))
                        .finally(() => setBusy(false));
                    }}
                  >
                    Volver a mostrar este significado
                  </button>
                </div>
              )}
              <div className="meaning-report-actions">
                <button
                  className="meaning-report-secondary"
                  type="button"
                  onClick={() =>
                    void reportContext(word)
                      .then(onContext)
                      .catch(() => setError('No pudimos actualizar el estado.'))
                  }
                >
                  <LibIcRetry size={15} />
                  Actualizar estado
                </button>
                <button className="meaning-report-cancel" type="button" onClick={close}>
                  Cerrar
                </button>
              </div>
              <button
                className="meaning-report-link"
                type="button"
                disabled={!context.enabled}
                onClick={() => setShowNew(true)}
              >
                Avisar algo distinto
              </button>
            </>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
              aria-label="Avisar un error"
            >
              <fieldset disabled={busy || !context.enabled}>
                <label className="meaning-report-field meaning-report-motive">
                  <span>Qué encontraste</span>
                  <span className="meaning-report-select">
                    <select
                      value={reason}
                      onChange={(e) => {
                        setReason(e.target.value as LexiconReportInput['reason']);
                        setComment('');
                        setField(undefined);
                        edit();
                      }}
                    >
                      <option value="wrong">Está mal</option>
                      <option value="inappropriate">Es impropio</option>
                      <option value="missing">Falta un significado</option>
                    </select>
                    <LibIcChevD size={15} />
                  </span>
                </label>
                {reason === 'wrong' && (
                  <label className="meaning-report-field">
                    <span>
                      Parte del significado <small>(opcional)</small>
                    </span>
                    <span className="meaning-report-select">
                      <select
                        value={field ?? ''}
                        onChange={(e) => {
                          setField((e.target.value as LexiconReportInput['field']) || undefined);
                          edit();
                        }}
                      >
                        <option value="">Sin especificar</option>
                        <option value="translation">Traducción</option>
                        <option value="definition">Definición</option>
                        <option value="example">Ejemplo</option>
                        <option value="level">Nivel</option>
                      </select>
                      <LibIcChevD size={15} />
                    </span>
                  </label>
                )}
                <div className="meaning-report-field">
                  <label htmlFor={commentId}>
                    {reason === 'missing' ? (
                      'Oración o explicación breve'
                    ) : (
                      <>
                        Comentario <small>(opcional)</small>
                      </>
                    )}
                  </label>
                  <textarea
                    id={commentId}
                    ref={textarea}
                    value={comment}
                    aria-describedby={`${helpId} ${counterId}`}
                    minLength={reason === 'missing' ? context.limits.missingMin : undefined}
                    required={reason === 'missing'}
                    maxLength={maxLength}
                    onChange={(e) => {
                      setComment(e.target.value);
                      edit();
                    }}
                  />
                  <div className="meaning-report-field-meta">
                    <p id={helpId} className="meaning-report-note">
                      {belowMinimum
                        ? `Al menos ${context.limits.missingMin} caracteres.`
                        : 'Sin copiar la charla completa.'}
                    </p>
                    <span
                      id={counterId}
                      className={`meaning-report-counter ${comment.length === maxLength ? 'at-limit' : ''}`}
                    >
                      <span className="sr-only">Caracteres: </span>
                      {comment.length}/{maxLength}
                    </span>
                  </div>
                </div>
                <div className="meaning-report-actions">
                  <button className="meaning-report-primary" type="submit" disabled={belowMinimum}>
                    {busy ? 'Enviando…' : request ? 'Reintentar' : 'Enviar aviso'}
                    <LibIcArrowR size={16} />
                  </button>
                  <button className="meaning-report-cancel" type="button" onClick={close}>
                    Cancelar
                  </button>
                </div>
              </fieldset>
            </form>
          )}
          {error && (
            <div className="meaning-report-error" role="alert">
              <LibIcFlag size={17} />
              <p>{error}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
